import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import posthog from 'posthog-js';

const BLUE  = 'var(--signal)';
const TEXT1 = 'var(--ink)';
const TEXT2 = 'var(--slate)';
const TEXT3 = 'var(--mute)';

const CATEGORIES = [
  'Vehicle and transportation',
  'Tools and equipment',
  'Insurance',
  'Phone and communications',
  'Protective gear and uniforms',
  'Training and licensing',
  'Marketing and advertising',
  'Software and subscriptions',
  'Office and admin',
  'Materials and supplies',
  'Travel and lodging',
  'Custom',
];

const FREQUENCIES = [
  { label: 'Monthly',  value: 'monthly' },
  { label: 'One-time', value: 'one_time' },
];

function StickyHeader({ navigate }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, backgroundColor: 'var(--card)',
      borderBottom: '0.5px solid rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center',
      padding: '0 20px', gap: 10,
    }}>
      <span
        onClick={() => navigate('/dashboard')}
        style={{ fontSize: 18, color: TEXT2, cursor: 'pointer', lineHeight: 1, paddingRight: 4, userSelect: 'none' }}
      >
        ←
      </span>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px' }}>
        <span style={{ color: TEXT1 }}>Tru</span>
        <span style={{ color: BLUE }}>Flow</span>
      </span>
    </div>
  );
}

function FocusInput({ value, onChange, placeholder, type = 'text' }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', height: 44,
        border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
        borderRadius: 8, padding: '0 12px',
        fontSize: 14, color: TEXT1,
        backgroundColor: 'var(--card)', outline: 'none',
        boxSizing: 'border-box',
      }}
    />
  );
}

function FocusSelect({ value, onChange, children }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', height: 44,
        border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
        borderRadius: 8, padding: '0 12px',
        fontSize: 14, color: value ? TEXT1 : TEXT3,
        backgroundColor: 'var(--card)', outline: 'none',
        boxSizing: 'border-box',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        cursor: 'pointer',
      }}
    >
      {children}
    </select>
  );
}

function AmountInput({ value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <span style={{
        position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
        fontSize: 14, color: TEXT2,
        pointerEvents: 'none',
      }}>
        $
      </span>
      <input
        type="number"
        value={value}
        onChange={onChange}
        placeholder="e.g. 800"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%', height: 44,
          border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
          borderRadius: 8, padding: '0 12px 0 24px',
          fontSize: 14, color: TEXT1,
          backgroundColor: 'var(--card)', outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function Field({ label, optional, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: TEXT1 }}>{label}</span>
        {optional && <span style={{ fontSize: 10, color: TEXT3 }}>Optional</span>}
      </div>
      {children}
    </div>
  );
}

export default function AddExpense() {
  const navigate = useNavigate();
  const [name, setName]           = useState('');
  const [category, setCategory]   = useState('');
  const [amount, setAmount]       = useState('');
  const [frequency, setFrequency] = useState('');
  const [notes, setNotes]         = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  async function handleSubmit(e) {
    e.preventDefault();
    if (!name || !category || !amount || !frequency) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/expenses', {
        name,
        category,
        amount: parseFloat(amount),
        frequency,
        notes: notes || null,
        date: null,
      });
      posthog.capture('expense_added');
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
      <StickyHeader navigate={navigate} />

      <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px 40px' }}>
        <h1 style={{ margin: '0 0 6px', fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: TEXT1 }}>Add expense</h1>
        <p style={{ margin: '0 0 24px', fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
          Track a recurring or one-time business expense.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <Field label="Expense name">
            <FocusInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Truck payment, Fuel, Insurance"
            />
          </Field>

          <Field label="Category">
            <FocusSelect value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="" disabled>Select a category…</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </FocusSelect>
          </Field>

          <Field label="Amount">
            <AmountInput value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>

          <Field label="Frequency">
            <FocusSelect value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              <option value="" disabled>Select frequency…</option>
              {FREQUENCIES.map(({ label, value }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </FocusSelect>
          </Field>

          <Field label="Notes" optional>
            <FocusInput
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details"
            />
          </Field>

          {error && (
            <p style={{
              color: 'var(--stop)', fontSize: 13,
              margin: '0 0 12px', padding: '10px 12px',
              backgroundColor: 'var(--stop-soft)', border: '1px solid var(--stop-border)',
              borderRadius: 8,
            }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              backgroundColor: loading ? 'rgba(24,95,165,0.6)' : BLUE,
              color: 'var(--on-signal)', borderRadius: 10,
              padding: 13, fontSize: 14, fontWeight: 500,
              border: 'none', cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading ? 'Saving…' : 'Add expense'}
          </button>
        </form>
      </div>
    </div>
  );
}
